import type { ReactElement } from "react";
import { cloneElement } from "react";
import type { HTMLChakraProps } from "@chakra-ui/react";
import { Box, Flex, chakra, forwardRef, Text } from "@chakra-ui/react";
import TextLink from "components/common/link/text-link";
import FacebookLogo from "components/icons/circle-icons/facebook-logo";
import LinkedinLogo from "components/icons/circle-icons/linkedin-logo";
import TwitterLogo from "components/icons/circle-icons/twitter-logo";
import {
  BasicInfoContainer,
  BasicInfoItem,
} from "components/profile/basic-info";
import ProfileCard, {
  ProfileCardHeader,
} from "components/profile/profile-card";
import getSiteUrl from "utils/get-site-url";
import { getBlogIcon, getTwitterUrl } from "utils/social";
import type { CompanyProfile } from "types/api/company/details/overview";

interface SocialIconLinkProps extends HTMLChakraProps<"a"> {
  href: string;
  label: string;
  size?: number;
  children: ReactElement;
}

const SocialIconLink = ({
  href,
  children,
  label,
  size = 6,
  ...props
}: SocialIconLinkProps) => {
  const icon = cloneElement(children, {
    w: "100%",
    h: "100%",
    color: "inherit",
  });

  return (
    <chakra.a
      aria-label={label}
      m="3px"
      href={href}
      target="_blank"
      lineHeight={1}
      w={size}
      h={size}
      display="block"
      rounded="full"
      color="blue.600"
      _hover={{
        color: "blue.700",
      }}
      _focus={{
        outline: "none",
        boxShadow: "outline",
      }}
      _active={{
        color: "blue.800",
      }}
      {...props}
    >
      {icon}
    </chakra.a>
  );
};

interface AboutCardProps {
  isLoaded: boolean;
  companyProfile: CompanyProfile;
  isIntegrated: boolean;
}

const AboutCard = forwardRef<AboutCardProps, "div">(
  ({ isLoaded, companyProfile, isIntegrated }, ref) => {
    const hasSocialLink =
      !!companyProfile?.socialBlog ||
      !!companyProfile?.socialLinkedin ||
      !!companyProfile?.socialFacebook ||
      !!companyProfile?.socialTwitter;

    const addressLine1 = [
      companyProfile?.headquartersAddr1,
      companyProfile?.headquartersAddr2,
    ]
      .filter(Boolean)
      .join(" ");

    const hasAddressLine1 = !!addressLine1;

    const addressLine2 = [
      companyProfile?.headquartersAddrCity,
      companyProfile?.headquartersAddrStateCode,
      companyProfile?.headquartersAddrZipCode,
      companyProfile?.headquartersAddrCountryCode,
    ]
      .filter(Boolean)
      .join(" ");

    const hasAddressLine2 = !!addressLine2;

    return (
      <ProfileCard>
        <ProfileCardHeader>About</ProfileCardHeader>

        <BasicInfoContainer ref={ref}>
          <BasicInfoItem isLoaded={isLoaded} label="Founders">
            {companyProfile?.companyFounders?.map((name) => (
              <Box key={name}>{name}</Box>
            ))}
          </BasicInfoItem>

          <BasicInfoItem
            isLoaded={isLoaded}
            label={isIntegrated ? "Integrated With" : "Parents"}
          >
            {companyProfile?.companyParents?.map(({ id, name, urlSlug }) =>
              urlSlug ? (
                <TextLink key={id} variant="truncate" href={urlSlug}>
                  {name}
                </TextLink>
              ) : (
                <Box key={id}>{name}</Box>
              )
            )}
          </BasicInfoItem>

          <BasicInfoItem isLoaded={isLoaded} label="Former Names">
            {companyProfile?.companyFormerNames?.map((name) => (
              <Box key={name}>{name}</Box>
            ))}
          </BasicInfoItem>

          <BasicInfoItem isLoaded={isLoaded} label="DBA">
            {companyProfile?.companyDbas?.map((dba) => (
              <Box key={dba}>{dba}</Box>
            ))}
          </BasicInfoItem>

          <BasicInfoItem isLoaded={isLoaded} label="AKA">
            {companyProfile?.companyAkas?.map((aka) => (
              <Box key={aka}>{aka}</Box>
            ))}
          </BasicInfoItem>

          <BasicInfoItem isLoaded={isLoaded} label="Address">
            {addressLine1}
            {hasAddressLine1 && hasAddressLine2 && <br />}
            {addressLine2}
          </BasicInfoItem>

          <BasicInfoItem isLoaded={isLoaded} label="Phone">
            {companyProfile?.headquartersAddrPhone && (
              <TextLink
                isExternal
                hasIcon
                href={`tel:${companyProfile.headquartersAddrPhone}`}
                variant="truncate"
              >
                {companyProfile.headquartersAddrPhone}
              </TextLink>
            )}
          </BasicInfoItem>

          <BasicInfoItem isLoaded={isLoaded} label="Website">
            {companyProfile?.companyWebsites?.map((companyWebsite) => (
              <Box key={companyWebsite.url}>
                <TextLink
                  isExternal
                  hasIcon
                  href={getSiteUrl(companyWebsite.url)}
                  variant="truncate"
                  truncateDirection="rtl"
                >
                  {companyWebsite.url}
                </TextLink>
                <Text as="span"
                  fontSize="sm"
                  fontWeight="500"
                >
                  {companyWebsite.status === "Active" ? null : companyWebsite.status === "" ? `(${  "N/A"  })` : `(${  companyWebsite.status  })`}
                </Text> 
              </Box>
            ))}
          </BasicInfoItem>

          <BasicInfoItem isLoaded={isLoaded} label="Social">
            {hasSocialLink && (
              <Flex m="-3px" mt={0}>
                {companyProfile?.socialBlog && (
                  <SocialIconLink
                    href={getSiteUrl(companyProfile.socialBlog)}
                    label="Blog Link"
                  >
                    {getBlogIcon(companyProfile?.socialBlog)}
                  </SocialIconLink>
                )}

                {companyProfile?.socialFacebook && (
                  <SocialIconLink
                    href={getSiteUrl(companyProfile.socialFacebook)}
                    label="Facebook Link"
                  >
                    <FacebookLogo />
                  </SocialIconLink>
                )}

                {companyProfile?.socialTwitter && (
                  <SocialIconLink
                    href={getTwitterUrl(companyProfile.socialTwitter)}
                    label="Twitter Link"
                  >
                    <TwitterLogo />
                  </SocialIconLink>
                )}

                {companyProfile?.socialLinkedin && (
                  <SocialIconLink
                    href={getSiteUrl(companyProfile.socialLinkedin)}
                    label="LinkedIn Link"
                  >
                    <LinkedinLogo />
                  </SocialIconLink>
                )}
              </Flex>
            )}
          </BasicInfoItem>
        </BasicInfoContainer>
      </ProfileCard>
    );
  }
);

export default AboutCard;
